import { standardSchemaResolver } from "@hookform/resolvers/standard-schema"
import { Trans } from "@lingui/react/macro"
import * as Sentry from "@sentry/tanstackstart-react"
import { useMutation, useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { createServerFn } from "@tanstack/react-start"
import { getRequest } from "@tanstack/react-start/server"
import { AlertCircle, FileUp, Loader2, Upload } from "lucide-react"
import { useState } from "react"
import { useForm, type ControllerRenderProps } from "react-hook-form"
import { z } from "zod"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { PageHeader } from "@/components/ui/PageHeader"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useTypedFlag } from "@/features/feature-flags/registry"
import {
  getStores,
  createStore,
  listStoreConfigurations,
} from "@/features/project-editor/server/projectServer"
import { StoreSelectionDialog } from "@/features/store-configuration/components/StoreSelectionDialog"
import { authFunctionMiddleware } from "@/lib/auth-middleware"

// Schema for the import form
const importLegacySchema = z.object({
  name: z.string().min(1, "Project name is required"),
  storeId: z.string().optional(),
  storeConfigurationId: z.string().optional(),
})

type ImportLegacyFormValues = z.infer<typeof importLegacySchema>

type StoreOption = {
  id: string
  name: string
  city: string
  fullAddress: string
  countryCode?: string
  locale?: string
  latestConfigurationId?: string
  latestConfigurationName?: string
  latestConfigurationValidFrom?: Date
}

// Server function to handle the blueprint import
const importBlueprintAction = createServerFn({ method: "POST" })
  .middleware([authFunctionMiddleware])
  .inputValidator((data: FormData) => data)
  .handler(async ({ data }) => {
    const { db } = await import("@/db/db")
    const { projectsTable, stores } = await import("@/db/app-schema")
    const { auth } = await import("@/lib/auth")
    const { logAuditEvent } = await import("@/features/audit-logs/server/auditLogger")
    const { saveProjectFile } = await import("@/lib/fileStorage")
    const { normalizeLocale } = await import("@/features/stores/core/utils/normalizeLocale")
    const { loadStoreAddressSnapshot } =
      await import("@/features/stores/server/storeAddressSnapshot.server")
    const { eq } = await import("drizzle-orm")

    const name = data.get("name") as string
    const storeId = data.get("storeId") as string | null
    const storeConfigurationId = data.get("storeConfigurationId") as string | null
    const clPdf = data.get("clPdf") as File | null
    const uoPdf = data.get("uoPdf") as File | null

    if (!name) {
      throw new Response("Project name is required", { status: 400 })
    }
    if (!clPdf || !uoPdf) {
      throw new Response("Both CL and UO PDF files are required", {
        status: 400,
      })
    }

    const request = getRequest()
    const session = await auth.api.getSession({
      headers: request.headers,
    })

    return Sentry.startSpan({ name: "Import Blueprint Server Action" }, async (span) => {
      span.setAttribute("otel.status", "ok")
      span.setAttribute("app.project.name", name)

      try {
        // Pull the store's locale so the imported project gets a default
        // configLocale — without this, exports later fall back to "nl" and
        // the optimize page would force the user to pick a locale by hand.
        let storeLocale: string | null = null
        if (storeId) {
          const [store] = await db
            .select({ locale: stores.locale })
            .from(stores)
            .where(eq(stores.id, storeId))
            .limit(1)
          if (store?.locale) storeLocale = normalizeLocale(store.locale)
        }

        // Snapshot the store's current address (BLU-589).
        const storeAddressSnapshot = storeId ? await loadStoreAddressSnapshot(storeId) : null

        // 1. Create the project
        const dataToInsert = {
          name,
          storeId: storeId || null,
          storeAddressSnapshot,
          author: session?.user?.name || session?.user?.email || "Unknown User",
          date: new Date(),
          country: "",
          location: "",
          metadata: {},
          configVersionId: storeConfigurationId || null,
          configLocale: storeLocale,
        }

        const [newProject] = await db
          .insert(projectsTable)
          .values(dataToInsert)
          .returning({ id: projectsTable.id })

        if (!newProject.id) {
          throw new Error("Failed to create project: No ID returned.")
        }

        span.setAttribute("app.project.id", newProject.id)

        // 2. Save the UO PDF as the project's main PDF
        await saveProjectFile(newProject.id, uoPdf, "pdf")

        // Update metadata to mark PDF as uploaded
        await db
          .update(projectsTable)
          .set({
            metadata: {
              ...dataToInsert.metadata,
              pdfFileUploaded: true,
            },
          })
          .where(eq(projectsTable.id, newProject.id))

        // 3. Call the backend import API
        const apiUrl = process.env.BLUEPRINT_API_URL || "http://localhost:5246"
        const apiKey = process.env.BLUEPRINT_API_KEY

        const importFormData = new FormData()
        importFormData.append("clPdf", clPdf)
        importFormData.append("uoPdf", uoPdf)
        importFormData.append("projectId", newProject.id)

        const importResponse = await fetch(`${apiUrl}/api/blueprint-import`, {
          method: "POST",
          headers: apiKey ? { "X-API-KEY": apiKey } : {},
          body: importFormData,
        })

        if (!importResponse.ok) {
          const errorText = await importResponse.text()

          // Provide user-friendly error messages based on error type
          if (errorText.includes("too large")) {
            throw new Error(
              "One or both PDF files are too large (maximum 50MB per file). Please ensure your files are within the size limit.",
            )
          } else if (errorText.includes("not a valid PDF")) {
            throw new Error(
              "One or both files are not valid PDF files. Please check that you've uploaded PDF documents.",
            )
          } else if (errorText.includes("timed out")) {
            throw new Error(
              "The import process took too long and was cancelled. This may happen with very complex blueprints. Please try again or contact support if the problem persists.",
            )
          } else if (errorText.includes("pageNumber")) {
            throw new Error("Invalid page number specified. Please ensure page numbers are valid.")
          } else {
            // Include the backend error message for other cases
            throw new Error(`Blueprint import failed: ${errorText}`)
          }
        }

        const importResult = (await importResponse.json()) as {
          success: boolean
          error?: string
          uoCroppedImage?: string
          uoCroppedX?: number
          uoCroppedY?: number
          uoCroppedWidth?: number
          uoCroppedHeight?: number
          phase1?: { success: boolean; rotation?: number; confidence?: number }
          phase2?: { success: boolean; shelfCount?: number; coveragePercent?: number }
          phase3?: { success: boolean; overlayCount?: number }
          phase4?: { success: boolean; assignmentCount?: number; unmappedCount?: number }
        }

        // 4. Save the cropped image if provided by the backend
        if (importResult.uoCroppedImage) {
          // Convert base64 to binary
          const binaryString = atob(importResult.uoCroppedImage)
          const bytes = new Uint8Array(binaryString.length)
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i)
          }
          const croppedImageBlob = new Blob([bytes], { type: "image/png" })
          const croppedImageFile = new File([croppedImageBlob], "cropped.png", {
            type: "image/png",
          })

          // Save as cropped.png for the project
          await saveProjectFile(newProject.id, croppedImageFile, "png")

          // Update metadata with crop area dimensions
          await db
            .update(projectsTable)
            .set({
              metadata: {
                ...dataToInsert.metadata,
                pdfFileUploaded: true,
                cropArea: {
                  x: importResult.uoCroppedX ?? 0,
                  y: importResult.uoCroppedY ?? 0,
                  width: importResult.uoCroppedWidth ?? 0,
                  height: importResult.uoCroppedHeight ?? 0,
                },
              },
            })
            .where(eq(projectsTable.id, newProject.id))
        }

        // 5. Log audit event (filter sensitive debug data)
        await logAuditEvent(
          {
            action: "project.import.blueprint",
            entityType: "project",
            entityId: newProject.id,
            entityName: name,
            summary: `Imported blueprint project "${name}"`,
            metadata: {
              storeId: storeId ?? undefined,
              // Only log summary statistics, not full debug data
              phase1Success: importResult.phase1?.success ?? false,
              phase1Rotation: importResult.phase1?.rotation,
              phase1Confidence: importResult.phase1?.confidence,
              phase2Success: importResult.phase2?.success ?? false,
              phase2ShelfCount: importResult.phase2?.shelfCount,
              phase2Coverage: importResult.phase2?.coveragePercent,
              phase3Success: importResult.phase3?.success ?? false,
              phase3OverlayCount: importResult.phase3?.overlayCount,
              phase4Success: importResult.phase4?.success ?? false,
              phase4AssignmentCount: importResult.phase4?.assignmentCount,
              phase4UnmappedCount: importResult.phase4?.unmappedCount,
              overallSuccess: importResult.success,
              error: importResult.error,
            },
          },
          request,
        )

        return { success: true, projectId: newProject.id }
      } catch (error) {
        span.setAttribute("otel.status", "error")

        if (error instanceof Error) {
          span.setAttribute("error.message", error.message)

          // Only report unexpected errors to Sentry (not validation errors)
          const isUserError =
            error.message.includes("too large") ||
            error.message.includes("not a valid PDF") ||
            error.message.includes("page number") ||
            error.message.includes("within the size limit")

          if (!isUserError) {
            Sentry.captureException(error)
          }

          throw new Error(error.message)
        }

        // Unknown error type - report to Sentry
        Sentry.captureException(error)
        throw new Error("An unknown error occurred during import.")
      }
    })
  })

export const Route = createFileRoute("/(authenticated)/projects/import-legacy/")({
  component: ImportLegacyProject,
})

function ImportLegacyProject() {
  const navigate = useNavigate({ from: Route.fullPath })
  const { value: blueprintImportEnabled, isLoading: isLoadingFlag } =
    useTypedFlag("blueprint-import")

  // Redirect to projects list if feature is disabled
  if (!isLoadingFlag && !blueprintImportEnabled) {
    navigate({ to: "/projects" })
    return null
  }

  const [serverError, setServerError] = useState<string | null>(null)
  const [showStoreDialog, setShowStoreDialog] = useState(false)
  const [selectedStore, setSelectedStore] = useState<StoreOption | null>(null)
  const [clFile, setClFile] = useState<File | null>(null)
  const [uoFile, setUoFile] = useState<File | null>(null)
  const [isImporting, setIsImporting] = useState(false)

  // Query for stores list
  const { data: stores = [] } = useQuery<StoreOption[]>({
    queryKey: ["stores"],
    queryFn: () => getStores() as unknown as Promise<StoreOption[]>,
  })

  const form = useForm<ImportLegacyFormValues>({
    resolver: standardSchemaResolver(importLegacySchema),
    defaultValues: {
      name: "",
      storeId: undefined,
      storeConfigurationId: undefined,
    },
    mode: "onChange",
  })

  const storeConfigsQuery = useQuery({
    queryKey: ["store-configs", selectedStore?.id],
    queryFn: () =>
      selectedStore
        ? listStoreConfigurations({ data: { storeId: selectedStore.id } })
        : Promise.resolve({ configurations: [] }),
    enabled: Boolean(selectedStore?.id),
  })

  // Store creation mutation
  const createStoreMutation = useMutation({
    mutationFn: createStore,
    onSuccess: (result) => {
      setSelectedStore({
        id: result.storeId,
        name: result.store.name,
        city: result.store.city,
        fullAddress: result.store.fullAddress,
        countryCode: (result as unknown as { store?: { countryCode?: string } })?.store
          ?.countryCode,
      })
      form.setValue("storeId", result.storeId)
      form.setValue("storeConfigurationId", undefined)
      setShowStoreDialog(false)
    },
  })

  // Handle store selection
  const handleSelectStore = (storeId: string) => {
    const store = stores.find((s) => s.id === storeId)
    if (store) {
      setSelectedStore(store)
      form.setValue("storeId", storeId)
      if (!form.getValues("name")) {
        form.setValue("name", store.name)
      }
      if (store.latestConfigurationId) {
        form.setValue("storeConfigurationId", store.latestConfigurationId)
      }
      setShowStoreDialog(false)
    }
  }

  const handleCreateStore = (storeData: {
    name: string
    city: string
    fullAddress: string
    countryCode?: string
  }) => {
    void createStoreMutation.mutate({ data: storeData })
  }

  const onSubmit = async (values: ImportLegacyFormValues) => {
    if (!clFile || !uoFile) {
      setServerError("Both CL (Blueprint) and UO (Original) PDF files are required")
      return
    }

    setServerError(null)
    setIsImporting(true)

    try {
      const formData = new FormData()
      formData.append("name", values.name)
      if (values.storeId) formData.append("storeId", values.storeId)
      if (values.storeConfigurationId)
        formData.append("storeConfigurationId", values.storeConfigurationId)
      formData.append("clPdf", clFile)
      formData.append("uoPdf", uoFile)

      const result = await importBlueprintAction({ data: formData })

      if (result.success && result.projectId) {
        await navigate({
          to: `/projects/edit/$projectId/optimize`,
          params: { projectId: result.projectId },
        })
      } else {
        setServerError("Import failed: unexpected response")
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : "Failed to import blueprint"
      setServerError(errorMessage)
      Sentry.captureException(error)
    } finally {
      setIsImporting(false)
    }
  }

  return (
    <div className="container max-w-2xl">
      <PageHeader title="Import existing blueprint" className="mb-4" />
      <p className="text-muted-foreground mb-6">
        <Trans>
          Import a legacy blueprint by uploading the CL (Blueprint) and UO (Original) PDF files. The
          system will automatically extract shelf assignments from the colored overlays.
        </Trans>
      </p>

      {serverError && (
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Import Error</AlertTitle>
          <AlertDescription>{serverError}</AlertDescription>
        </Alert>
      )}

      <Form {...form}>
        <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)} className="space-y-6">
          {/* Project Name */}
          <FormField
            control={form.control}
            name="name"
            render={({
              field,
            }: {
              field: ControllerRenderProps<ImportLegacyFormValues, "name">
            }) => (
              <FormItem>
                <FormLabel>
                  <Trans>Project Name</Trans>
                </FormLabel>
                <FormControl>
                  <Input placeholder="Name your project" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Store Selection */}
          <FormField
            control={form.control}
            name="storeId"
            render={() => (
              <FormItem>
                <FormLabel>
                  <Trans>Store</Trans>
                </FormLabel>
                <FormControl>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowStoreDialog(true)}
                    className="w-full justify-start"
                  >
                    {selectedStore ? (
                      <div className="flex flex-col items-start">
                        <span className="font-medium">{selectedStore.name}</span>
                        <span className="text-muted-foreground text-sm">
                          {selectedStore.city} • {selectedStore.fullAddress}
                        </span>
                      </div>
                    ) : (
                      <Trans>Select a store...</Trans>
                    )}
                  </Button>
                </FormControl>
                <FormDescription>
                  <Trans>Choose the store location for this project (optional).</Trans>
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Store Configuration */}
          {selectedStore && (
            <FormField
              control={form.control}
              name="storeConfigurationId"
              render={({
                field,
              }: {
                field: ControllerRenderProps<ImportLegacyFormValues, "storeConfigurationId">
              }) => (
                <FormItem>
                  <FormLabel>
                    <Trans>Store Configuration</Trans>
                  </FormLabel>
                  <FormControl>
                    <Select
                      value={field.value}
                      onValueChange={(val: string) => field.onChange(val)}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            storeConfigsQuery.isLoading
                              ? "Loading configurations..."
                              : "Select configuration..."
                          }
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {(storeConfigsQuery.data?.configurations ?? []).map(
                          (config: { id: string; name: string; validFrom: string | Date }) => (
                            <SelectItem key={config.id} value={config.id}>
                              {config.name} (
                              {new Intl.DateTimeFormat("en", {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                              }).format(new Date(config.validFrom))}
                              )
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {/* CL PDF Upload */}
          <FormItem>
            <FormLabel>
              <Trans>CL PDF (Blueprint with colors)</Trans>
            </FormLabel>
            <FormControl>
              <div
                className={`flex cursor-pointer flex-col items-center rounded-lg border-2 border-dashed p-6 transition-colors ${
                  clFile
                    ? "border-green-500 bg-green-50 dark:bg-green-950"
                    : "hover:border-primary border-gray-300"
                }`}
                onClick={() => document.getElementById("cl-pdf-input")?.click()}
              >
                <FileUp className="mb-2 h-8 w-8 text-gray-400" />
                {clFile ? (
                  <span className="font-medium text-green-600">{clFile.name}</span>
                ) : (
                  <span className="text-muted-foreground">
                    <Trans>Click to select CL PDF file</Trans>
                  </span>
                )}
                <input
                  id="cl-pdf-input"
                  type="file"
                  accept=".pdf"
                  className="hidden"
                  onChange={(e) => setClFile(e.target.files?.[0] || null)}
                />
              </div>
            </FormControl>
            <FormDescription>
              <Trans>The blueprint PDF with colored category overlays (CL file).</Trans>
            </FormDescription>
          </FormItem>

          {/* UO PDF Upload */}
          <FormItem>
            <FormLabel>
              <Trans>UO PDF (Original floor plan)</Trans>
            </FormLabel>
            <FormControl>
              <div
                className={`flex cursor-pointer flex-col items-center rounded-lg border-2 border-dashed p-6 transition-colors ${
                  uoFile
                    ? "border-green-500 bg-green-50 dark:bg-green-950"
                    : "hover:border-primary border-gray-300"
                }`}
                onClick={() => document.getElementById("uo-pdf-input")?.click()}
              >
                <FileUp className="mb-2 h-8 w-8 text-gray-400" />
                {uoFile ? (
                  <span className="font-medium text-green-600">{uoFile.name}</span>
                ) : (
                  <span className="text-muted-foreground">
                    <Trans>Click to select UO PDF file</Trans>
                  </span>
                )}
                <input
                  id="uo-pdf-input"
                  type="file"
                  accept=".pdf"
                  className="hidden"
                  onChange={(e) => setUoFile(e.target.files?.[0] || null)}
                />
              </div>
            </FormControl>
            <FormDescription>
              <Trans>The original floor plan PDF without colors (UO file).</Trans>
            </FormDescription>
          </FormItem>

          {/* Submit Button */}
          <Button type="submit" disabled={isImporting || !clFile || !uoFile} className="w-full">
            {isImporting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                <Trans>Importing...</Trans>
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                <Trans>Import Blueprint</Trans>
              </>
            )}
          </Button>
        </form>
      </Form>

      {/* Store Selection Dialog */}
      <StoreSelectionDialog
        open={showStoreDialog}
        onOpenChange={setShowStoreDialog}
        onSelectStore={handleSelectStore}
        onCreateNewStore={handleCreateStore}
        existingStores={stores}
        isLoading={createStoreMutation.isPending}
      />
    </div>
  )
}
